import { TestingContextPageScopeProvider } from "@/flokit/utils/testing-context/page-scope";
import { Atom, swap, useAtom } from "@dbeining/react-atom";
import { cx } from "class-variance-authority";
import { nanoid } from "nanoid";
import { Dialog } from "radix-ui";
import React from "react";
import { FiX } from "react-icons/fi";

type ModalChild = () => React.ReactNode;

type Stack = ModalOptions[];
interface ModalOptions {
  width?: number;
  mobileFriendly?: boolean;
  id?: string;
  content: () => React.ReactNode;
  preventAutoFocus?: boolean;
  preventCloseOnClickOutside?: boolean;
}

const ModalAtom = Atom.of<Stack | null>([]);

export const ModalHeader = (props: {
  title?: string;
  variant?: "small" | "default";
}) => {
  return (
    <div
      className={cx(
        "relative flex w-full items-center justify-between pt-6",
        !props.variant || props.variant === "default" ? "pl-6 pr-4" : "",
      )}
    >
      {props.title && (
        <h1 className="text-3xl font-semibold text-charcoal">{props.title}</h1>
      )}
      <button
        onClick={() => ModalService.closeModal()}
        className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-white hover:bg-[#F9F9FA]"
      >
        <FiX size={20} />
      </button>
    </div>
  );
};

export const ModalManager = () => {
  const stack = useAtom(ModalAtom);
  if (!stack || !stack.length) {
    return null;
  }

  return (
    <>
      {stack.map((ModalComponent, i) => {
        const index = i + 1;

        const overlayZIndex = index * 1000 + 5000; // overlay will be 1000, 2000, 3000, etc.
        const modalZIndex = overlayZIndex + 1; // modal will be 1001, 2001, 3001, etc so overlay should be on top of it if stacked
        const isMobileFriendly = ModalComponent.mobileFriendly;
        return (
          <TestingContextPageScopeProvider
            key={ModalComponent.id}
            scope={`modal-${index}`}
          >
            <Dialog.Root
              open={ModalComponent !== null}
              onOpenChange={() => {
                ModalService.closeModal();
              }}
            >
              <Dialog.Portal>
                <Dialog.Overlay
                  onClick={() => {
                    if (ModalComponent.preventCloseOnClickOutside) return;
                    ModalService.closeModal();
                  }}
                  style={{
                    zIndex: overlayZIndex,
                  }}
                  className="fixed inset-0 bg-overlay outline-none focus:outline-none active:outline-none"
                />
                <Dialog.Content
                  onPointerDownOutside={(e) => {
                    e.preventDefault();

                    if (ModalComponent.preventCloseOnClickOutside) return;

                    // only pop if the click was outside the current modal
                    if (ModalComponent.id === stack[stack.length - 1].id) {
                      ModalService.closeModal();
                    }
                  }}
                  onEscapeKeyDown={(e) => {
                    if (ModalComponent.preventCloseOnClickOutside)
                      return e.preventDefault();
                  }}
                  style={{
                    zIndex: modalZIndex,
                  }}
                  onOpenAutoFocus={(e) => {
                    if (ModalComponent.preventAutoFocus) {
                      e.preventDefault();
                    }
                  }}
                  className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 outline-none focus:outline-none active:outline-none"
                  // Fix dismissing modal when dropdown menu is open causing page to be unresponsive
                  // https://github.com/shadcn-ui/ui/issues/1859#issuecomment-2589432274
                  onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    document.body.style.pointerEvents = "";
                  }}
                >
                  <div
                    style={{ width: ModalComponent.width ?? "auto" }}
                    className={cx(
                      "mx-auto flex max-h-screen flex-col outline-none focus:outline-none active:outline-none",
                      isMobileFriendly
                        ? "items-center justify-center sm:animate-fadeIn sm:animate-animated sm:animate-faster"
                        : // Animated transition looks weird for full screen modals on mobile
                          "animate-fadeIn animate-animated animate-faster",
                    )}
                  >
                    {ModalComponent.content()}
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </TestingContextPageScopeProvider>
        );
      })}
    </>
  );
};

export const ModalService = {
  setPreventCloseOnClickOutside: (value: boolean) => {
    swap(ModalAtom, (stack) => {
      if (!stack || !stack.length) return stack;
      const last = stack[stack.length - 1];
      return [
        ...stack.slice(0, -1),
        { ...last, preventCloseOnClickOutside: value },
      ];
    });
  },
  openModal: (
    modal: ModalChild,
    // Mobile friendly is a stopgap solution to the problem of the modal not
    // respecting max width on mobile.
    // Because we already have a lot of modals in the app, we need to make sure
    // not to affect those until we can set time to update them and test them.
    options?: Omit<ModalOptions, "content">,
  ) =>
    swap(ModalAtom, (stack) => {
      return [
        ...stack,
        {
          content: modal,
          width: options ? options.width : null,
          id: nanoid(),
          mobileFriendly: options?.mobileFriendly ?? false,
          preventAutoFocus: options?.preventAutoFocus ?? false,
          preventCloseOnClickOutside:
            options?.preventCloseOnClickOutside ?? false,
        },
      ];
    }),
  closeModal: () => {
    // remove last modal
    swap(ModalAtom, (stack) => {
      return (stack ?? []).slice(0, -1);
    });
  },
};
